import React, { memo } from "react";
import SlideOutSidebar from "../../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Form, Formik, FormikProps } from "formik";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSFormTextarea from "../../../../ui-components/form/textarea/xgsFormTextarea";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ImportantMessage, { ImportantMessageTheme } from "../../../../ui-components/molecules/important-message/importantMessage";
import { AccessorialApprovalStatus, OperationsAccessorialApprovalRequest, OperationsAccessorialApprovalRequestValidationSchema, ShipmentWorkItemAccessorial, ShipmentWorkItemReviewStatus } from "../../../../app/data/shipment-reconciliation/models";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import { OperationsAccessorialApprovalState, OperationsReviewListState, OperationsReviewSummaryState, ShipmentWorkItemAccessorialsState } from "../../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import { operationsAccessorialApprovalSelector, submitOperationsAccessorialApproval } from "../../../../slices/shipment-reconciliation/operations-review/operationAccessorialsApprovalSlice";
import { changeOperationsReviewListLocally, getOperationsReviewListSelector } from "../../../../slices/shipment-reconciliation/operations-review/getOperationsReviewListSlice";
import { changeOperationsReviewSummaryLocally, getOperationsReviewSummarySelector } from "../../../../slices/shipment-reconciliation/operations-review/getOperationsReviewSummarySlice";
import { shipmentWorkItemAccessorialsSelector } from "../../../../slices/shipment-reconciliation/shipmentWorkItemAccessorialsSlice";
import "./operationsAccessorialApproval.scss";


interface OperationAccessorialApprovalProps {
  shipmentWorkItemId: number,
  accessorialId: string,
  approvalStatus: AccessorialApprovalStatus,
  accessorialName: string,
  accessorialRate: string,
  onClose: any,
  onSuccess: any
}

const OperationAccessorialApproval: React.FC<OperationAccessorialApprovalProps> = memo(({ shipmentWorkItemId, accessorialId, approvalStatus, accessorialName, accessorialRate, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const formRef = React.useRef<FormikProps<OperationsAccessorialApprovalRequest>>(null);

  const operationsAccessorialApprovalState: OperationsAccessorialApprovalState = useSelector(
    operationsAccessorialApprovalSelector
  );
  const operationsReviewList: OperationsReviewListState = useSelector(
    getOperationsReviewListSelector
  );
  const operationsReviewSummary: OperationsReviewSummaryState = useSelector(
    getOperationsReviewSummarySelector
  );
  const shipmentWorkItemAccessorials: ShipmentWorkItemAccessorialsState = useSelector(
    shipmentWorkItemAccessorialsSelector
  );

  const adjustOperationsReviewSummaryAndList = () => {
    let currentReviewSummary = { ...operationsReviewSummary.summary };
    let currentReviewList = [...operationsReviewList.shipmentWorkItems];
    let currentAccessorialsList = [...shipmentWorkItemAccessorials.accessorials];

    let affectedWorkItemIndex = currentReviewList.findIndex((ele) => ele.id === shipmentWorkItemId);
    if (affectedWorkItemIndex === -1) return;

    let affectedWorkItem = { ...currentReviewList[affectedWorkItemIndex] };

    // check if the there is any pending accessorial
    let isReviewComplete = true;
    if (currentAccessorialsList && currentAccessorialsList.length) {
      currentAccessorialsList.forEach(accessorial => {
        if (accessorial && accessorial.id !== accessorialId && accessorial.operationApprovalStatus === AccessorialApprovalStatus.PENDING) {
          isReviewComplete = false;
        }
      });
    }

    if (isReviewComplete) {
      if (affectedWorkItem.operationsReviewStatus !== ShipmentWorkItemReviewStatus.REVIEWED) {
        currentReviewSummary.compltedOperationsReview += 1;
        currentReviewSummary.pendingAppliedRate -= (+accessorialRate);
        currentReviewSummary.pendingOperationsReview -= 1;
        if (approvalStatus === AccessorialApprovalStatus.ACCEPTED) {
          currentReviewSummary.acceptedAppliedRate += (+accessorialRate);
        } else {
          currentReviewSummary.rejectedAppliedRate += (+accessorialRate);
        }
        affectedWorkItem.operationsReviewStatus = ShipmentWorkItemReviewStatus.REVIEWED;
      }
    } else {
      if (affectedWorkItem.operationsReviewStatus !== ShipmentWorkItemReviewStatus.REVIEWED) {
        currentReviewSummary.compltedOperationsReview += 1;
        currentReviewSummary.pendingAppliedRate -= (+accessorialRate);
        if (approvalStatus === AccessorialApprovalStatus.ACCEPTED) {
          currentReviewSummary.acceptedAppliedRate += (+accessorialRate);
        } else {
          currentReviewSummary.rejectedAppliedRate += (+accessorialRate);
        }
      }
    }
    let affectedAccessorials: ShipmentWorkItemAccessorial[] = [];
    if (affectedWorkItem.accessorials && affectedWorkItem.accessorials.length) {
      affectedWorkItem.accessorials.forEach((accessorial: ShipmentWorkItemAccessorial) => {
        if (accessorial) {
          let affectedAccessorial = { ...accessorial };
          if (accessorial.id === accessorialId) {
            affectedAccessorial.operationApprovalStatus = approvalStatus;
          }
          affectedAccessorials.push(affectedAccessorial);
        }

      })
    }
    affectedWorkItem.accessorials = affectedAccessorials;
    currentReviewList[affectedWorkItemIndex] = affectedWorkItem;
    dispatch(changeOperationsReviewListLocally({
      shipmentWorkItems: currentReviewList
    }));
    dispatch(changeOperationsReviewSummaryLocally({
      summary: currentReviewSummary
    }));
  }

  const onSubmit = (data: OperationsAccessorialApprovalRequest) => {
    if (data.approvalStatus === AccessorialApprovalStatus.RJECTED && !data.notes) {
      toast.error("Please provide rejection reason while rejecting an accessorial");
      return;
    }
    dispatch(submitOperationsAccessorialApproval(
      shipmentWorkItemId,
      accessorialId,
      data,
      () => {
        adjustOperationsReviewSummaryAndList();
        toast.info("Accessorial approval status stored successfully.");
        onClose();
        onSuccess && onSuccess();
      },
      (error) => {
        toast.error(error);
      }
    ))
  }

  return (
    <SlideOutSidebar
      header="Accessorial Approval"
      onClose={onClose}
      size="extrasmall"
      show={!!(shipmentWorkItemId)}
    >
      <div className="xgs-operations-accessorial-approval">
        <ImportantMessage
          theme={ImportantMessageTheme.BLUE}
        >You are going to {approvalStatus === AccessorialApprovalStatus.ACCEPTED ? 'Accept' : 'Reject'} the accessorial {accessorialName}.
          {approvalStatus === AccessorialApprovalStatus.ACCEPTED ? 'This will push the accessorial and probill to the Billing team to apply your reviewed charges.' :
            `This Acton will remove the accessorial and probill from the Billing review queue and the charges will not be applied to the probill`
          }
        </ImportantMessage>
        <Formik
          initialValues={{
            approvalStatus,
            notes: ""
          }}
          onSubmit={onSubmit}
          validationSchema={OperationsAccessorialApprovalRequestValidationSchema}
          innerRef={formRef}
        >
          {(props: FormikProps<OperationsAccessorialApprovalRequest>) => (
            <Form className="operation-approval-form">
              <XGSFormInput
                type="text"
                name="approvalStatus"
                label="Approval Status"
                value={approvalStatus}
                labelMode={LabelModes.column}
                disabled={true}
              />
              <XGSFormTextarea
                name="notes"
                label="Notes"
                required={false}
                rows={3}
                counter={500}
              />
              <Button
                type="submit"
                theme={ButtonThemes.blue}
                spinner={operationsAccessorialApprovalState.loading}
                disabled={operationsAccessorialApprovalState.loading}
                className="operation-approval-form__submit"
              >
                Submit
              </Button>
            </Form>
          )}
        </Formik>
        <div className="submit-accessorial-approval-actions">
        </div>
      </div>
    </SlideOutSidebar>
  );
});

export default OperationAccessorialApproval;
